import React, { useState, useEffect, Suspense } from 'react';
import { createRoom, getToken, getRoom } from './services/api';
import './App.css';

// Lazy load MeetingRoom to reduce initial bundle size (livekit-client is huge)
const MeetingRoom = React.lazy(() => import('./components/MeetingRoom'));

const LIVEKIT_URL = process.env.REACT_APP_LIVEKIT_URL || 'ws://localhost:7880';

function App() {
  const [screen, setScreen] = useState('welcome'); // 'welcome' | 'meeting' | 'restore'
  const [roomName, setRoomName] = useState('');
  const [participantName, setParticipantName] = useState('');
  const [token, setToken] = useState(null);
  const [meetingCode, setMeetingCode] = useState('');
  const [savedSession, setSavedSession] = useState(null);
  const [hideControls, setHideControls] = useState(false);
  const [hideLeave, setHideLeave] = useState(false);
  const [hideChat, setHideChat] = useState(false);
  const [highlightSpeaker, setHighlightSpeaker] = useState(null);
  const [autoJoinError, setAutoJoinError] = useState(null);
  const [isAutoJoining, setIsAutoJoining] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const [videoDefault, setVideoDefault] = useState(false);
  const [audioDefault, setAudioDefault] = useState(true);

  // Check for saved session and URL parameters on mount
  useEffect(() => {
    const handleAutoJoin = async () => {
      // Check URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const roomFromUrl = urlParams.get('room');
      const nameFromUrl = urlParams.get('name');
      const tokenFromUrl = urlParams.get('token');
      const autoJoin = urlParams.get('autoJoin') === 'true';
      const isCreatorParam = urlParams.get('creator') === 'true';
      const hideControlsParam = urlParams.get('hideControls') === 'true';
      const hideLeaveParam = urlParams.get('hideLeave') === 'true';
      const hideChatParam = urlParams.get('hideChat') === 'true';
      const highlightSpeakerParam = urlParams.get('highlightSpeaker');
      const videoParam = urlParams.get('video');
      const audioParam = urlParams.get('audio');

      // Set UI parameters
      if (hideControlsParam) setHideControls(true);
      if (hideLeaveParam) setHideLeave(true);
      if (hideChatParam) setHideChat(true);
      if (highlightSpeakerParam) setHighlightSpeaker(highlightSpeakerParam);

      console.log('[DEBUG] URL Params:', { video: videoParam, audio: audioParam });

      // Set media defaults (default is video off, audio on if not specified)
      const isVideoOn = videoParam === 'true';
      const isAudioOn = audioParam !== 'false'; // Default to true if not 'false'

      setVideoDefault(isVideoOn);
      setAudioDefault(isAudioOn);
      console.log('[DEBUG] Media Defaults set to:', { video: isVideoOn, audio: isAudioOn });

      // Auto-join logic (for Mafia integration)
      if (autoJoin && roomFromUrl && nameFromUrl) {
        console.log('[INFO] Auto-join initiated:', { room: roomFromUrl, user: nameFromUrl, creator: isCreatorParam });
        setIsAutoJoining(true);
        setIsCreator(isCreatorParam);

        try {
          let finalToken = tokenFromUrl;
          let finalName = nameFromUrl;

          // If no token provided, generate one
          if (!finalToken) {
            // Check if room exists
            let roomExists = false;
            try {
              await getRoom(roomFromUrl);
              console.log('[INFO] Room found:', roomFromUrl);
              roomExists = true;
            } catch (error) {
              console.log('[INFO] Room not found:', roomFromUrl);
            }

            // If room doesn't exist and we're the creator - create it
            if (!roomExists && isCreatorParam) {
              console.log('[INFO] Creating room as creator...');
              try {
                await createRoom(roomFromUrl, nameFromUrl);
                console.log('[INFO] Room created successfully:', roomFromUrl);
              } catch (createError) {
                console.error('[ERROR] Failed to create room:', createError);
                // Continue anyway - maybe room was just created by someone else
              }
            }

            // If room still doesn't exist and we're NOT the creator - wait for it
            if (!roomExists && !isCreatorParam) {
              let retries = 0;
              const maxRetries = 10;
              while (retries < maxRetries) {
                try {
                  await getRoom(roomFromUrl);
                  console.log('[INFO] Room found after waiting:', roomFromUrl);
                  roomExists = true;
                  break;
                } catch (error) {
                  retries++;
                  console.log(`[INFO] Waiting for room... (${retries}/${maxRetries})`);
                  await new Promise(resolve => setTimeout(resolve, 2000));
                }
              }
            }

            // Generate token (with retry for "already in room" error)
            try {
              const tokenData = await getToken(roomFromUrl, nameFromUrl);
              finalToken = tokenData.token;
              console.log('[INFO] Token generated for auto-join');
            } catch (error) {
              const errorMsg = error.response?.data?.detail || error.message || '';

              // If user already in room - try with suffix
              if (errorMsg.includes('уже находится в комнате') || errorMsg.includes('already in room')) {
                console.log('[INFO] User already in room, trying with suffix...');
                try {
                  // Add invisible char (Zero Width Space) to make identity unique but display name identical
                  // Using multiple ZWS chars based on random to ensure uniqueness across multiple retries
                  const zwsObj = '\u200B';
                  const suffixedName = `${nameFromUrl}${zwsObj.repeat(Math.floor(Math.random() * 3) + 1)}`;
                  console.log('[INFO] Trying with name:', suffixedName);
                  const tokenData = await getToken(roomFromUrl, suffixedName);
                  finalToken = tokenData.token;
                  // Update name logic to use the one that worked
                  finalName = suffixedName;
                } catch (retryError) {
                  const retryErrorDetail = retryError.response?.data?.detail || 'Не удалось получить токен (повторно).';
                  throw new Error(retryErrorDetail);
                }
              } else {
                throw new Error(errorMsg || 'Не удалось получить токен для подключения.');
              }
            }
          }

          // Set state and connect
          setRoomName(roomFromUrl);
          // If name was not updated by retry logic, use original
          if (!finalToken) throw new Error('Token generation failed'); // Should be caught already

          setParticipantName(finalName);
          setToken(finalToken);
          saveMeetingSession(roomFromUrl, finalName);
          setScreen('meeting');
          setIsAutoJoining(false);
          console.log('[INFO] Auto-join successful');
        } catch (error) {
          console.error('[ERROR] Auto-join failed:', error);
          setAutoJoinError(error.message || 'Не удалось подключиться к видеовстрече');
          setIsAutoJoining(false);
        }
        return;
      }

      // Invite link - just pre-fill the room code, user enters their name
      if (roomFromUrl) {
        setMeetingCode(roomFromUrl);
        // Don't pre-fill name - user must enter unique name
      }

      // Check for saved session and auto-restore
      const saved = localStorage.getItem('meetingSession');
      if (saved) {
        try {
          const session = JSON.parse(saved);
          const sessionAge = Date.now() - session.timestamp;
          if (sessionAge < 24 * 60 * 60 * 1000) {
            // Auto-restore session without asking
            console.log('[INFO] Auto-restoring session:', session);
            setIsAutoJoining(true);

            try {
              // Check if room still exists
              const room = await getRoom(session.roomName);
              if (!room) {
                console.log('[INFO] Room no longer exists, clearing session');
                localStorage.removeItem('meetingSession');
                setIsAutoJoining(false);
                return;
              }

              // Get new token
              const tokenData = await getToken(session.roomName, session.participantName);

              setRoomName(session.roomName);
              setParticipantName(session.participantName);
              setToken(tokenData.token);
              setIsCreator(session.isCreator || false);
              setScreen('meeting');
              setIsAutoJoining(false);
              console.log('[INFO] Session restored successfully');
            } catch (error) {
              console.error('[ERROR] Failed to restore session:', error);
              localStorage.removeItem('meetingSession');
              setAutoJoinError('Не удалось восстановить встречу');
              setIsAutoJoining(false);
            }
          } else {
            localStorage.removeItem('meetingSession');
          }
        } catch (e) {
          localStorage.removeItem('meetingSession');
        }
      }
    };

    handleAutoJoin();
  }, []);

  const saveMeetingSession = (roomName, participantName, isCreator = false) => {
    const session = {
      roomName,
      participantName,
      isCreator,
      timestamp: Date.now(),
    };
    localStorage.setItem('meetingSession', JSON.stringify(session));
  };

  const clearMeetingSession = () => {
    localStorage.removeItem('meetingSession');
    setSavedSession(null);
  };

  const handleCreateMeeting = async () => {
    const name = participantName.trim();

    if (!name) {
      alert('Введите ваше имя');
      return;
    }

    if (name.length < 2) {
      alert('Имя должно содержать минимум 2 символа');
      return;
    }

    if (name.length > 30) {
      alert('Имя не должно превышать 30 символов');
      return;
    }

    try {
      const room = await createRoom(null, name);
      const tokenData = await getToken(room.name, name);

      setRoomName(room.name);
      setMeetingCode(room.name);
      setToken(tokenData.token);
      setIsCreator(true); // Создатель комнаты
      saveMeetingSession(room.name, name, true);
      setScreen('meeting');
    } catch (error) {
      console.error('Failed to create meeting:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
      alert(errorMessage);
    }
  };

  const handleJoinMeeting = async () => {
    const name = participantName.trim();
    const code = meetingCode.trim();

    if (!name || !code) {
      alert('Введите имя и код встречи');
      return;
    }

    if (name.length < 2) {
      alert('Имя должно содержать минимум 2 символа');
      return;
    }

    if (name.length > 30) {
      alert('Имя не должно превышать 30 символов');
      return;
    }

    try {
      // Проверяем, существует ли комната
      const room = await getRoom(code);
      if (!room) {
        alert('Комната с таким кодом не найдена. Проверьте код или создайте новую встречу.');
        return;
      }

      const tokenData = await getToken(code, name);

      setRoomName(code);
      setToken(tokenData.token);
      setIsCreator(false); // Не создатель
      saveMeetingSession(code, name, false);
      setScreen('meeting');
    } catch (error) {
      console.error('Failed to join meeting:', error);
      // Extract error message from API response
      const errorMessage = error.response?.data?.detail || error.message || 'Неизвестная ошибка';
      alert(errorMessage);
    }
  };

  const handleLeaveMeeting = () => {
    clearMeetingSession();
    setScreen('welcome');
    setToken(null);
    setRoomName('');
    setMeetingCode('');

    // Clear URL params to prevent auto-rejoin on refresh
    const url = new URL(window.location);
    if (url.search) {
      url.search = '';
      window.history.replaceState({}, '', url);
    }
  };

  const handleRestoreSession = async () => {
    if (!savedSession) return;

    try {
      // Check if room still exists
      const room = await getRoom(savedSession.roomName);
      if (!room) {
        alert('Встреча уже завершена');
        clearMeetingSession();
        setScreen('welcome');
        return;
      }

      // Get new token
      const tokenData = await getToken(savedSession.roomName, savedSession.participantName);

      setRoomName(savedSession.roomName);
      setParticipantName(savedSession.participantName);
      setToken(tokenData.token);
      setIsCreator(savedSession.isCreator || false);
      setScreen('meeting');
    } catch (error) {
      console.error('Failed to restore session:', error);
      alert('Не удалось восстановить встречу');
      clearMeetingSession();
      setScreen('welcome');
    }
  };

  const handleDeclineRestore = () => {
    clearMeetingSession();
    setScreen('welcome');
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(meetingCode);
    alert('Код скопирован!');
  };

  // Auto-join loading screen
  if (isAutoJoining) {
    return (
      <div className="App">
        <div className="welcome-screen">
          <div className="welcome-card">
            <h1>🔄 Подключение...</h1>
            <p>Автоматическое подключение к видеовстрече</p>
            {autoJoinError && (
              <div className="error-message">
                <p>❌ {autoJoinError}</p>
                <button className="btn-primary" onClick={() => window.location.reload()}>
                  🔄 Попробовать снова
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (screen === 'meeting') {
    return (
      <Suspense fallback={
        <div className="meeting-room loading">
          <p>Загрузка модулей видеосвязи...</p>
        </div>
      }>
        <MeetingRoom
          roomName={roomName}
          participantName={participantName}
          token={token}
          livekitUrl={LIVEKIT_URL}
          onLeave={handleLeaveMeeting}
          hideControls={hideControls}
          hideLeave={hideLeave}
          hideChat={hideChat}
          highlightSpeaker={highlightSpeaker}
          isCreator={isCreator}
          videoEnabled={videoDefault}
          audioEnabled={audioDefault}
        />
      </Suspense>
    );
  }

  if (screen === 'restore') {
    return (
      <div className="App">
        <div className="welcome-screen">
          <div className="welcome-card">
            <h1>🔄 Восстановление сессии</h1>
            <p>Вы были во встрече. Хотите вернуться?</p>

            <div className="session-info">
              <p><strong>Комната:</strong> {savedSession?.roomName}</p>
              <p><strong>Ваше имя:</strong> {savedSession?.participantName}</p>
            </div>

            <button className="btn-primary" onClick={handleRestoreSession}>
              ✅ Вернуться во встречу
            </button>

            <button className="btn-secondary" onClick={handleDeclineRestore}>
              ❌ Начать заново
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="welcome-screen">
        <div className="welcome-card">
          <h1>Trexon Calls</h1>
          <p>Создайте встречу или присоединитесь к существующей</p>

          <div className="input-group">
            <label>Ваше имя</label>
            <input
              type="text"
              value={participantName}
              onChange={(e) => setParticipantName(e.target.value)}
              placeholder="Введите ваше имя"
            />
          </div>

          <button className="btn-primary" onClick={handleCreateMeeting}>
            🎬 Создать встречу
          </button>

          <div className="divider">или</div>

          <div className="input-group">
            <label>Код встречи</label>
            <input
              type="text"
              value={meetingCode}
              onChange={(e) => setMeetingCode(e.target.value)}
              placeholder="Введите код встречи"
            />
          </div>

          <button className="btn-secondary" onClick={handleJoinMeeting}>
            🚪 Присоединиться
          </button>
        </div>
      </div>
    </div>
  );
}

export default App;

